// kWh aprovado no mês aberto por canal (Comercial, Parceiros,
// Empresa Parceira, Licitação), no período escolhido e no anterior no mesmo ponto.
// Usa o funil do CRM (mesma conta da aba Comercial e do KPI "Energia em contratos
// aprovados") sobre as negociações que o Painel Executivo já carrega.
//
// Pedido do Felipe em 28/10/2026: a licitação entra no total, mas a tela mostra
// de onde veio cada parte (a Prefeitura de Morrinhos, sozinha, foi 151.928 kWh
// em SET/2026).
import { useMemo } from 'react';
import type { Profile } from '../../types';
import { computeEventFunnel, type EventFunnelRow } from '../CRM/Analytics/eventFunnel';
import { energiaPorCanal, separarPorCanal, type ParteDoCanal } from '../../src/lib/visaoGeral/comercial';
import type { PeriodoVisaoGeral } from '../../src/lib/visaoGeral/periodo';

export interface EnergiaPorCanal {
  atual: ParteDoCanal[];
  anterior: ParteDoCanal[];
  totalAtual: number;
  totalAnterior: number;
  licitacaoAtual: number;
  licitacaoAnterior: number;
}

const aprovadoEntre = (rows: EventFunnelRow[], inicio: string, fim: string) => {
  const etapa = computeEventFunnel(rows, 'personalizado', { inicio, fim }).find((e) => e.key === 'termo_aprovado');
  return { qtd: etapa?.leads ?? 0, kwh: etapa?.kwh ?? 0 };
};

export function useEnergiaPorCanal(
  dados: EventFunnelRow[],
  profilesById: Map<string, Profile>,
  periodo: Pick<PeriodoVisaoGeral, 'inicio' | 'fim' | 'anterior'>,
): EnergiaPorCanal | null {
  return useMemo(() => {
    if (dados.length === 0) return null;
    const porCanal = separarPorCanal(dados, profilesById);
    const atual = energiaPorCanal(porCanal, (rows) => aprovadoEntre(rows, periodo.inicio, periodo.fim));
    const anterior = energiaPorCanal(porCanal, (rows) => aprovadoEntre(rows, periodo.anterior.inicio, periodo.anterior.fim));
    const soma = (p: ParteDoCanal[]) => p.reduce((t, x) => t + x.kwh, 0);
    const lic = (p: ParteDoCanal[]) => p.find((x) => x.canal === 'licitacao')?.kwh ?? 0;
    return {
      atual, anterior,
      totalAtual: soma(atual), totalAnterior: soma(anterior),
      licitacaoAtual: lic(atual), licitacaoAnterior: lic(anterior),
    };
  }, [dados, profilesById, periodo.inicio, periodo.fim, periodo.anterior.inicio, periodo.anterior.fim]);
}

const kwhTexto = (v: number) => `${Math.round(v).toLocaleString('pt-BR')} kWh`;
const pct = (v: number) => `${(Math.abs(v) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/**
 * As linhas "de onde veio" do cartão de energia e a nota sobre a licitação.
 * Mostra só os canais com kWh no mês ou no anterior; a licitação fica destacada
 * quando pesa na comparação.
 */
export function composicaoDaEnergia(e: EnergiaPorCanal, rotuloAnterior: string): {
  composicao: { rotulo: string; valor: string; destaque?: boolean; dica?: string }[];
  nota?: string;
} {
  const composicao = e.atual
    .filter((p, i) => p.kwh > 0 || e.anterior[i].kwh > 0)
    .map((p, i) => ({
      rotulo: p.rotulo,
      valor: kwhTexto(p.kwh),
      destaque: p.canal === 'licitacao' && (p.kwh > 0 || e.licitacaoAnterior > 0),
      dica: `${p.contratos} ${p.contratos === 1 ? 'contrato' : 'contratos'} · ${rotuloAnterior}: ${kwhTexto(e.anterior.find((x) => x.canal === p.canal)?.kwh ?? 0)}`,
    }));
  if (e.licitacaoAtual <= 0 && e.licitacaoAnterior <= 0) return { composicao };
  const semAtual = e.totalAtual - e.licitacaoAtual;
  const semAnterior = e.totalAnterior - e.licitacaoAnterior;
  const variacao = semAnterior > 0 ? semAtual / semAnterior - 1 : null;
  const comparacao = variacao == null ? '' : `, ${pct(variacao)} ${variacao >= 0 ? 'acima' : 'abaixo'}`;
  const anterior = `${rotuloAnterior.charAt(0).toLowerCase()}${rotuloAnterior.slice(1)}`;
  return {
    composicao,
    nota: `Licitação: ${kwhTexto(e.licitacaoAtual)} neste mês e ${kwhTexto(e.licitacaoAnterior)} em ${anterior}. `
      + `Sem licitação: ${kwhTexto(semAtual)} contra ${kwhTexto(semAnterior)}${comparacao}.`,
  };
}
